import { useEffect, useState } from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'
import { api } from './lib/api.ts'
import { clearSessionHint, hasSessionHint } from './lib/authHint.ts'
import { LoginGate } from './components/LoginGate.tsx'
import { AppShell } from './components/AppShell.tsx'
import { HubLanding } from './components/HubLanding.tsx'
import { DepartmentPage } from './components/DepartmentPage.tsx'
import { Dashboard } from './components/Dashboard.tsx'
import { ToolPage } from './components/ToolPage.tsx'
import { SettingsPage } from './components/SettingsPage.tsx'
import { TopicPage } from './components/TopicPage.tsx'
import { KnowledgeTestPage } from './components/KnowledgeTestPage.tsx'
import { MeetingGuidePage } from './components/MeetingGuidePage.tsx'
import { DocumentPage } from './components/DocumentPage.tsx'

export default function App() {
  // Optimistic, from a cookie we can read synchronously: painting is decided in
  // the first render instead of a round trip later. Blocking on that round trip
  // is what made every load flash "Loading…" before any content.
  //
  // The server remains the authority — the check below can demote us, and every
  // API route enforces auth no matter what we chose to render.
  const [authed, setAuthed] = useState(hasSessionHint)

  useEffect(() => {
    let live = true
    api
      .session()
      .then((ok) => {
        if (!live) return
        setAuthed(ok)
        // A hint outliving its session would flash the shell on every load.
        if (!ok) clearSessionHint()
      })
      // A network error is no proof the session ended, so the hint stays: this
      // visitor gets the gate now and an optimistic paint once they're back.
      .catch(() => {
        if (live) setAuthed(false)
      })
    return () => {
      live = false
    }
  }, [])

  // The gate wraps the routes rather than being one of them, so the URL survives
  // sign-in: a deep link to /tools/:id shows the gate, then lands on that tool.
  if (!authed) return <LoginGate onSuccess={() => setAuthed(true)} />

  return (
    <Routes>
      <Route element={<AppShell />}>
        <Route path="/" element={<HubLanding />} />
        <Route path="/departments/:dept" element={<DepartmentPage />} />
        <Route path="/dashboard" element={<Dashboard />} />
        {/* CatalogLanding (the pre-Task-16 flat listing) is retired: it had none of
            the fixes buildEntries brought — dead links, unmarked drafts, raw tag
            dump. Redirect rather than 404, so an old bookmark still lands somewhere
            useful. The hub (/) and department pages now cover everything it did. */}
        <Route path="/tools" element={<Navigate to="/" replace />} />
        <Route path="/tools/:id" element={<ToolPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/topics/:topicId" element={<TopicPage />} />
        {/* These two fixed paths must precede :artefactId below, or it captures them. */}
        <Route path="/topics/:topicId/knowledge-test" element={<KnowledgeTestPage />} />
        <Route path="/topics/:topicId/meeting-guide" element={<MeetingGuidePage />} />
        <Route path="/topics/:topicId/:artefactId" element={<DocumentPage />} />
        {/* Catches anything unrecognised. */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  )
}
